<template>
    <!--
        THE FIELD VIEW'S FRAME (docs/API.md §16): a phone in one hand at a TV, so no sidebar and no
        drawer — a slim header (the hotel, switched here when there is more than one; the colour
        mode; the person) over one column at most `max-w-xl` wide, `px-4` like the guest app.

        Same viewport model as the console's shell (`Default.vue`): exactly `h-dvh`, and `<main>`
        is what scrolls, so the router's scroll-to-top (it scrolls `main`) works here too and a
        page's sticky action bar sits at the foot of the visible screen.
    -->
    <div class="flex h-dvh flex-col overflow-hidden">
        <header class="h-(--ui-header-height) shrink-0 flex items-center gap-2 border-b border-default bg-(--ui-bg-chrome) px-3">
            <img src="/logo.svg" alt="" class="size-7 shrink-0">

            <div class="min-w-0 flex-1">
                <USelectMenu
                    v-if="tenantItems.length > 1"
                    :model-value="tid"
                    :items="tenantItems"
                    value-key="value"
                    :search-input="tenantItems.length > 6 ? { placeholder: $t('general.search') } : false"
                    variant="ghost"
                    size="lg"
                    class="w-full max-w-64 font-semibold"
                    :aria-label="$t('field_view.hotel')"
                    @update:model-value="switchTenant"
                />
                <p v-else class="truncate px-1 text-base font-semibold text-highlighted">{{ tenantName }}</p>
            </div>

            <ColorModeToggle />

            <UDropdownMenu :items="userMenu" :content="{ align: 'end' }">
                <UButton color="neutral" variant="ghost" class="p-1" :aria-label="userName">
                    <span class="flex size-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{{ userInitials }}</span>
                </UButton>
            </UDropdownMenu>
        </header>

        <main class="flex-1 overflow-y-auto">
            <div class="mx-auto flex min-h-full w-full max-w-xl flex-col gap-3 px-4 pt-3">
                <router-view />
            </div>
        </main>
    </div>
</template>

<script lang="ts" setup>
    import type { DropdownMenuItem } from '@nuxt/ui'
    import { computed, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import { TenantRole } from '@/enums/tenant'
    import ColorModeToggle from '@/layouts/components/ColorModeToggle.vue'
    import { initials, roleLabel } from '@/libs/user'
    import { useAppStore } from '@/stores/app'
    import { useTenantStore } from '@/stores/tenant'
    import type { SelectOption } from '@/types/Common'

    const { t }       = useI18n()
    const route       = useRoute()
    const router      = useRouter()
    const appStore    = useAppStore()
    const tenantStore = useTenantStore()

    const tid = computed((): string => typeof route.params.tid === 'string' ? route.params.tid : '')

    // A provider admin picks among every tenant: the list is asked once.
    watch(() => appStore.isProviderAdmin(), (provider) => { if (provider) void tenantStore.loadTenants() }, { immediate: true })

    /** The hotels this person can open the field view of: every one for a provider admin, else theirs but the kitchen's. */
    const tenantItems = computed((): SelectOption[] => appStore.isProviderAdmin()
        ? tenantStore.tenants.map((item) => ({ label: item.name, value: item.id }))
        : appStore.memberships()
            .filter((item) => item.role !== TenantRole.Kitchen)
            .map((item) => ({ label: item.name, value: item.id })))

    const tenantName = computed((): string => tenantStore.tenant?.id === tid.value ? tenantStore.tenant.name : '')

    /** Another hotel: the same page there, the list from a device's page. */
    const switchTenant = (value: string) => {
        if (!value || value === tid.value) return

        const name = route.name === 'field-pair' ? 'field-pair' : 'field'

        void router.push({ name, params: { tid: value } })
    }

    const userName     = computed((): string => appStore.userData?.name || appStore.userData?.username || '')
    const userInitials = computed((): string => initials(userName.value))

    const userMenu = computed((): DropdownMenuItem[][] => {
        const role = appStore.isProviderAdmin() ? t('role.provider_admin') : roleLabel(appStore.roleIn(tid.value))

        return [
            [{ label: userName.value, description: role, type: 'label' }],
            [
                // The whole console, for whoever has more there than the screens.
                ...(appStore.isTechnician(tid.value) ? [] : [{ label: t('field_view.full_console'), icon: 'i-lucide-layout-dashboard', onSelect: () => { void router.push({ name: 'overview', params: { tid: tid.value } }) } }]),
                { label: t('nav.settings'), icon: 'i-lucide-settings', onSelect: () => { void router.push({ name: 'settings' }) } },
            ],
            // Installing a house of boxes is a laptop's page (docs/PROVISIONING.md): admins and technicians.
            ...(appStore.canPair(tid.value) ? [[{
                label:       t('field_view.provisioning'),
                description: t('field_view.provisioning_help'),
                icon:        'i-lucide-package-plus',
                onSelect:    () => { void router.push({ name: 'devices', params: { tid: tid.value, tab: 'provisioning' } }) },
            }]] : []),
            [{
                label:    t('general.sign_out'),
                icon:     'i-lucide-log-out',
                onSelect: () => {
                    appStore.setLogout()
                    void router.push({ name: 'login' })
                },
            }],
        ]
    })
</script>
